import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface InputTextPennylaneOption {
  name: string;
  country: string;
}

export interface InputTextDialogData {
  title: string;
  message: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue?: string;
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  patternErrorMessage?: string;
  /** Si défini et non vide : sélecteur Pennylane obligatoire au-dessus du champ texte */
  pennylaneOptions?: InputTextPennylaneOption[];
  pennylaneSelectLabel?: string;
}

export interface InputTextDialogResult {
  confirmed: boolean;
  value?: string;
  pennylane_country?: string | null;
}

@Component({
  selector: 'app-input-text-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    MatIconModule
  ],
  template: `
    <div
      class="dialog-container"
      [class.has-pennylane-select]="data.pennylaneOptions && data.pennylaneOptions.length > 0">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      
      <div mat-dialog-content class="dialog-content">
        <div class="pennylane-block" *ngIf="data.pennylaneOptions && data.pennylaneOptions.length > 0">
          <mat-form-field
            appearance="outline"
            class="full-width-field pennylane-field"
            [class.pennylane-field-error]="pennylaneErrorMessage">
            <mat-label>{{ data.pennylaneSelectLabel || 'Pennylane account' }}</mat-label>
            <mat-select
              [(ngModel)]="selectedPennylaneCountry"
              (selectionChange)="onPennylaneChange()"
              panelClass="input-text-dialog-pennylane-panel">
              <mat-option value="">-- Select --</mat-option>
              <mat-option *ngFor="let opt of data.pennylaneOptions" [value]="opt.country">
                {{ opt.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <div class="pennylane-inline-error" *ngIf="pennylaneErrorMessage">{{ pennylaneErrorMessage }}</div>
        </div>

        <div class="message-text" [innerHTML]="data.message"></div>
        
        <mat-form-field appearance="outline" class="full-width-field">
          <mat-label>{{ data.inputLabel || 'Enter value' }}</mat-label>
          <input 
            matInput 
            [(ngModel)]="inputValue"
            [placeholder]="data.inputPlaceholder || ''"
            [maxlength]="data.maxLength"
            (keyup.enter)="onConfirm()"
            #inputField>
          <mat-icon matPrefix class="input-icon">edit</mat-icon>
          <mat-hint *ngIf="data.maxLength" align="end">
            {{ inputValue?.length || 0 }}/{{ data.maxLength }}
          </mat-hint>
        </mat-form-field>
        
        <div class="error-message" *ngIf="errorMessage">
          <mat-icon>error</mat-icon>
          {{ errorMessage }}
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          class="action-btn cancel-btn bg-warn"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button
          class="action-btn confirm-btn bg-primary"
          [disabled]="!isValid()"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 400px;
    }

    .dialog-container.has-pennylane-select {
      min-width: 520px;
      max-width: 96vw;
    }

    @media (max-width: 600px) {
      .dialog-container.has-pennylane-select {
        min-width: 0;
        width: 100%;
      }
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      color: #2a3547;
      margin: 0;
      padding: 24px 24px 16px 24px;
      line-height: 1.4;
    }

    .dialog-content {
      /* Espace en haut : le label flottant outline dépasse au-dessus du trait — sans padding il est rogné */
      padding: 20px 24px 24px 24px !important;
      color: #5a6a85;
      overflow: visible !important;
    }

    :host ::ng-deep .mat-mdc-dialog-content.dialog-content {
      overflow: visible !important;
    }

    .pennylane-block {
      margin-bottom: 16px;
      margin-top: 4px;
      padding-top: 2px;
      text-align: left;
      width: 100%;
    }

    .pennylane-field {
      width: 100%;
    }

    .pennylane-field ::ng-deep .mat-mdc-text-field-wrapper {
      overflow: visible;
    }

    .pennylane-field ::ng-deep .mat-mdc-form-field-infix {
      width: 100%;
      min-width: 0;
    }

    .pennylane-field ::ng-deep .mat-mdc-select {
      width: 100%;
      max-width: 100%;
    }

    .pennylane-field ::ng-deep .mat-mdc-select-value {
      max-width: 100%;
    }

    .pennylane-field ::ng-deep .mat-mdc-select-trigger {
      width: 100%;
    }

    .pennylane-inline-error {
      color: #dc2626;
      font-size: 12px;
      font-weight: 500;
      margin-top: 4px;
      margin-bottom: 4px;
    }

    .pennylane-field.pennylane-field-error ::ng-deep .mdc-notched-outline__leading,
    .pennylane-field.pennylane-field-error ::ng-deep .mdc-notched-outline__notch,
    .pennylane-field.pennylane-field-error ::ng-deep .mdc-notched-outline__trailing {
      border-color: #ef4444 !important;
      border-width: 2px !important;
    }

    .message-text {
      font-size: 14px;
      line-height: 1.6;
      color: #5a6a85;
      margin-bottom: 20px;
    }

    /* Style pour le texte en HTML */
    .message-text ::ng-deep strong,
    .message-text ::ng-deep b {
      color: #5d87ff;
      font-weight: 600;
    }

    .message-text ::ng-deep em,
    .message-text ::ng-deep i {
      font-style: italic;
      color: #5d87ff;
    }

    .message-text ::ng-deep .highlight {
      background-color: #fff9e6;
      padding: 2px 6px;
      border-radius: 4px;
      color: #f39c12;
      font-weight: 500;
    }

    /* Champ de saisie */
    .full-width-field {
      width: 100%;
      margin-bottom: 8px;
    }

    .input-icon {
      color: #5d87ff;
      margin-right: 8px;
    }

    ::ng-deep .mat-mdc-form-field {
      font-size: 14px;
    }

    ::ng-deep .mat-mdc-text-field-wrapper {
      background-color: #f9fafb;
    }

    ::ng-deep .mat-mdc-form-field-focus-overlay {
      background-color: transparent;
    }

    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__leading,
    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__notch,
    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled) .mdc-notched-outline__trailing {
      border-color: #d1d5db;
    }

    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled):hover .mdc-notched-outline__leading,
    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled):hover .mdc-notched-outline__notch,
    ::ng-deep .mdc-text-field--outlined:not(.mdc-text-field--disabled):hover .mdc-notched-outline__trailing {
      border-color: #5d87ff;
    }

    ::ng-deep .mat-mdc-form-field.mat-focused .mdc-notched-outline__leading,
    ::ng-deep .mat-mdc-form-field.mat-focused .mdc-notched-outline__notch,
    ::ng-deep .mat-mdc-form-field.mat-focused .mdc-notched-outline__trailing {
      border-color: #5d87ff !important;
      border-width: 2px !important;
    }

    ::ng-deep .mat-mdc-form-field.mat-focused .mat-mdc-floating-label {
      color: #5d87ff !important;
    }

    /* Message d'erreur */
    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      padding: 12px;
      background-color: #fef2f2;
      border-left: 3px solid #ef4444;
      border-radius: 6px;
      color: #dc2626;
      font-size: 13px;
      animation: slideIn 0.3s ease;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .error-message mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    /* Actions */
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 0 24px 24px 24px !important;
      margin: 0 !important;
    }

    .action-btn {
      min-width: 120px;
      padding: 8px 24px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      text-transform: none;
      letter-spacing: 0.5px;
      transition: all 0.3s ease;
    }

    .action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .bg-primary {
      background-color: #5d87ff !important;
      color: white !important;
    }

    .bg-primary:hover:not(:disabled) {
      background-color: #4570ea !important;
      box-shadow: 0 4px 12px rgba(93, 135, 255, 0.3);
    }

    .bg-warn {
      background-color: #ff6692 !important;
      color: white !important;
    }

    .bg-warn:hover {
      background-color: #ff4d7d !important;
      box-shadow: 0 4px 12px rgba(255, 102, 146, 0.3);
    }
  `]
})
export class InputTextDialogComponent {
  inputValue: string = '';
  errorMessage: string = '';
  selectedPennylaneCountry = '';
  pennylaneErrorMessage = '';

  constructor(
    public dialogRef: MatDialogRef<InputTextDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InputTextDialogData
  ) {
    // Initialiser avec la valeur par défaut si fournie
    if (data.inputValue) {
      this.inputValue = data.inputValue;
    }
  }

  onPennylaneChange(): void {
    this.pennylaneErrorMessage = '';
  }

  ngAfterViewInit(): void {
    // Auto-focus sur le champ de saisie sans causer de scroll
    setTimeout(() => {
      const inputField = document.querySelector('input[matInput]') as HTMLInputElement;
      if (inputField) {
        // Sauvegarder la position de scroll actuelle
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        
        // Essayer d'utiliser preventScroll si supporté
        try {
          inputField.focus({ preventScroll: true });
        } catch (e) {
          // Fallback pour les navigateurs qui ne supportent pas preventScroll
          inputField.focus();
        }
        
        // Restaurer la position de scroll si elle a changé
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          if (currentScrollY !== scrollY) {
            window.scrollTo(0, scrollY);
          }
        });
      }
    }, 100);
  }

  isValid(): boolean {
    this.errorMessage = '';
    this.pennylaneErrorMessage = '';

    if (this.data.pennylaneOptions && this.data.pennylaneOptions.length > 0) {
      if (!this.selectedPennylaneCountry || !String(this.selectedPennylaneCountry).trim()) {
        return false;
      }
    }

    // Vérifier si le champ est requis
    if (this.data.required !== false && (!this.inputValue || this.inputValue.trim() === '')) {
      return false;
    }

    // Vérifier la longueur minimale
    if (this.data.minLength && this.inputValue.length < this.data.minLength) {
      return false;
    }

    // Vérifier la longueur maximale
    if (this.data.maxLength && this.inputValue.length > this.data.maxLength) {
      return false;
    }

    // When a pattern is set, confirm stays disabled until input is non-empty and matches
    if (this.data.pattern) {
      if (!this.inputValue || this.inputValue.trim() === '') {
        return false;
      }
      const regex = new RegExp(this.data.pattern);
      if (!regex.test(this.inputValue)) {
        return false;
      }
    }

    return true;
  }

  onConfirm(): void {
    if (!this.isValid()) {
      if (this.data.pennylaneOptions && this.data.pennylaneOptions.length > 0 &&
        (!this.selectedPennylaneCountry || !String(this.selectedPennylaneCountry).trim())) {
        this.pennylaneErrorMessage = 'Please select a Pennylane account';
      }
      // Afficher un message d'erreur spécifique
      if (this.data.required !== false && (!this.inputValue || this.inputValue.trim() === '')) {
        this.errorMessage = 'This field is required';
      } else if (this.data.minLength && this.inputValue.length < this.data.minLength) {
        this.errorMessage = `Minimum length is ${this.data.minLength} characters`;
      } else if (this.data.maxLength && this.inputValue.length > this.data.maxLength) {
        this.errorMessage = `Maximum length is ${this.data.maxLength} characters`;
      } else if (this.data.pattern && this.inputValue) {
        this.errorMessage = this.data.patternErrorMessage || 'Invalid format';
      }
      return;
    }

    const result: InputTextDialogResult = {
      confirmed: true,
      value: this.inputValue.trim(),
      pennylane_country:
        this.data.pennylaneOptions && this.data.pennylaneOptions.length > 0
          ? String(this.selectedPennylaneCountry).trim().toLowerCase()
          : undefined
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    const result: InputTextDialogResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }
}

